import { Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Navigate, Outlet, useParams } from "react-router";

import { ApiError } from "@/api/errors";
import { useSelectTenant, useSession } from "@/api/queries/auth";
import { useReleaseQueueCount } from "@/api/queries/salesOrders";
import { useTenantContext } from "@/api/queries/tenant";
import { AgentButton, AgentProvider } from "@/components/agent/AgentProvider";
import { GuidanceProvider, useGuidance } from "@/components/guidance/GuidanceProvider";
import { AppShell } from "@/components/shell/AppShell";
import { ImpersonationBanner } from "@/components/shell/ImpersonationBanner";
import { NotificationBell } from "@/components/shell/NotificationBell";
import type { NavMode, NavSection } from "@/components/shell/nav";
import { QuickCreateMenu } from "@/components/shell/QuickCreateMenu";
import { UniversalSearch } from "@/components/shell/UniversalSearch";
import { UserMenu } from "@/components/shell/UserMenu";
import { WorkspaceSwitcher } from "@/components/shell/WorkspaceSwitcher";
import { EmptyState, ErrorState, IconButton, PageLoader } from "@/components/ui";
import { useMembershipPreference, useMembershipPreferences } from "@/hooks/usePreferences";
import { TenantScopeContext, type TenantScope } from "@/hooks/useTenant";
import { applyTenantAccent } from "@/lib/branding";

import { tenantSectionFromPath, tenantSections } from "./navigation";
import { LiveStatusIndicator, LiveUpdatesProvider } from "./live/LiveUpdates";
import { quickCreateActions } from "./quickCreate";

export function TenantLayout() {
  const { slug = "" } = useParams();
  const context = useTenantContext(slug);
  const session = useSession();
  const selectTenant = useSelectTenant();
  const selected = useRef<string | null>(null);

  // Remember the workspace (last-used + login branding hint). Grants nothing server-side.
  useEffect(() => {
    if (context.isSuccess && selected.current !== slug) {
      selected.current = slug;
      selectTenant.mutate(slug);
    }
  }, [context.isSuccess, slug, selectTenant]);

  useEffect(() => {
    applyTenantAccent(context.data?.tenant.palette);
    return () => applyTenantAccent(null);
  }, [context.data?.tenant.palette]);

  if (context.isLoading) return <PageLoader label="Loading workspace" />;
  if (context.error instanceof ApiError && context.error.code === "surface_access_denied") return <Navigate to="/no-access" replace />;
  if (context.error instanceof ApiError && context.error.status === 404) {
    return (
      <div className="p-8">
        <EmptyState
          title="Workspace not found"
          description="It doesn't exist or you don't have access to it."
          action={
            <a className="text-sm font-medium text-accent-text underline" href="/workspaces">
              Choose a workspace
            </a>
          }
        />
      </div>
    );
  }
  if (context.isError || !context.data || !session.data?.user) {
    return (
      <div className="p-8">
        <ErrorState error={context.error} onRetry={() => context.refetch()} />
      </div>
    );
  }
  return <TenantShell slug={slug} scope={context.data} />;
}

function TenantShell({ slug, scope }: { slug: string; scope: NonNullable<ReturnType<typeof useTenantContext>["data"]> }) {
  const { data: session } = useSession();
  useMembershipPreferences(slug, scope.preferences);
  const [mode, setMode] = useMembershipPreference(slug, "nav_mode");
  const permissions = useMemo(() => new Set(scope.permissions), [scope.permissions]);
  const can = useCallback((code: string) => permissions.has(code), [permissions]);
  const value = useMemo<TenantScope>(() => ({ slug, context: scope, can }), [slug, scope, can]);
  const [recent] = useMembershipPreference(slug, "recent_records");
  // Orders waiting for someone who may release them: a count beside Sales › Release orders.
  const releaseQueue = `/t/${slug}/sales/release-orders`;
  const toRelease = useReleaseQueueCount(slug, can("sales_orders.view_order") && can("sales_orders.release")).data?.count ?? 0;
  const sections = useMemo(
    () =>
      tenantSections(slug, can).map((section) => ({
        ...section,
        items: section.items.map((i) => (i.to === releaseQueue ? { ...i, badge: toRelease } : i)),
        recent: (recent ?? []).filter((r) => r.section === section.key).map((r) => ({ label: r.label, kind: r.kind, to: `/t/${slug}/${r.path}` })),
      })),
    [slug, can, recent, releaseQueue, toRelease],
  );
  const sectionFromPath = useCallback((path: string) => tenantSectionFromPath(path), []);

  return (
    <TenantScopeContext.Provider value={value}>
      <GuidanceProvider key={slug} slug={slug} sectionFromPath={sectionFromPath} passive={Boolean(session?.impersonation)}>
        <LiveUpdatesProvider key={slug} slug={slug}>
        <AgentProvider key={slug}>
        <AppShell
          sections={sections}
          mode={mode as NavMode}
          onModeChange={setMode}
          workspaceLabel={scope.tenant.name}
          workspace={(compact) => (
            <WorkspaceSwitcher
              compact={compact}
              name={scope.tenant.name}
              logoUrl={scope.tenant.logo_url}
              currentSlug={slug}
              memberships={session?.memberships ?? []}
              platform={(session?.platform.capabilities.length ?? 0) > 0}
              impersonating={Boolean(session?.impersonation)}
              subtitle={scope.groups.map((g) => g.name).join(", ") || "Member"}
            />
          )}
          railTools={<AgentButton />}
          railFooter={<TenantRailFooter slug={slug} />}
          banner={session?.impersonation ? <ImpersonationBanner impersonation={session.impersonation} /> : undefined}
          header={(compact) => <TenantHeader slug={slug} sections={sections} can={can} compact={compact} />}
          status={<LiveStatusIndicator />}
        >
          <Outlet />
        </AppShell>
        </AgentProvider>
        </LiveUpdatesProvider>
      </GuidanceProvider>
    </TenantScopeContext.Provider>
  );
}

/** Over the page: universal search and the + menu. */
function TenantHeader({ slug, sections, can, compact }: { slug: string; sections: NavSection[]; can: (code: string) => boolean; compact: boolean }) {
  const actions = useMemo(() => quickCreateActions(slug, can), [slug, can]);
  return (
    <>
      <div className={compact ? undefined : "flex min-w-0 flex-1 items-center"}>
        <UniversalSearch slug={slug} sections={sections} compact={compact} />
      </div>
      <QuickCreateMenu actions={actions} compact={compact} />
      {/* On a phone there's no rail: the assistant sits in the top bar. */}
      {compact && <AgentButton size="bar" />}
    </>
  );
}

function TenantRailFooter({ slug }: { slug: string }) {
  const { data: session } = useSession();
  const guidance = useGuidance();
  if (!session?.user) return null;
  return (
    <>
      <NotificationBell slug={slug} />
      <IconButton label="What's new" tooltipSide="right" icon={<Sparkles className="size-[18px]" />} onClick={guidance.openWhatsNew} />
      <UserMenu user={session.user} profilePath={`/t/${slug}/profile`} onOpenWhatsNew={guidance.openWhatsNew} />
    </>
  );
}
